import { MutableRefObject } from 'react';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  NormalizedCacheObject
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import config from 'config';
import { Tokens } from 'modules/oauth/types/Tokens';
import { RepliconOAuth } from 'modules/oauth/RepliconOAuth';
import { updateTokenFromRefreshToken } from 'modules/oauth/useOAuth';
import { isTokenValid, getIdentityFromToken } from 'modules/oauth/tokenStore';
import { delay } from 'utils/delay';
import { extractError, getExtraFromExtension } from '../utils/sentry';
import { sentryGraphContext } from './sentryGraphContext';
import { OperationMetadata } from './operation-metadata';
export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        groupsSearch: {
          merge: true
        },
        analyticsMetadata: {
          merge: true
        }
      }
    },
    TimeSignalSource: {
      keyFields: false
    }
  }
});

export const createClient = (
  tokenRef: MutableRefObject<Tokens | null>
): ApolloClient<NormalizedCacheObject> => {
  const httpLink = createHttpLink({
    uri: config.graphApiUrl
  });

  const authLink = setContext((_, { headers }) => {
    const token = tokenRef.current!.accessToken;
    Sentry.addBreadcrumb({
      data: { token: token?.substring(1, 10) },
      level: 'info'
    });
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        'X-Replicon-Application': `time-intelligence-web;v=${window.version}`
      }
    };
  });

  const provider = new RepliconOAuth();
  let tokenRefreshInProgress = false;

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const context = operation.getContext();
    const operationData = new OperationMetadata(operation);
    const { response } = context;
    let ecId: string | null = null;
    if (response && response.headers) {
      ecId = response.headers.get('x-execution-correlation-id');
    }
    const isUnAuthenticatedError = (networkError as any)?.statusCode === 401;
    Sentry.addBreadcrumb({
      data: { ecId, status: (networkError as any)?.statusCode },
      level: 'info'
    });
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const { message, locations, path, extensions: { code } = {} } = err;
        Sentry.captureException(extractError(err), {
          tags: {
            ecId,
            operation: operationData.getFullName()
          },
          extra: getExtraFromExtension(err)
        });

        console.error(
          `[GraphQL error]: Operation: ${operationData.getFullName()} ECID: ${ecId} Message: ${message}, Location: ${locations}, Path: ${path}, code: ${code}`
        );
      }
    }

    if (networkError) {
      if (isUnAuthenticatedError) {
        const identity = getIdentityFromToken(tokenRef.current?.accessToken);
        Sentry.addBreadcrumb({
          data: {
            hasValidToken: isTokenValid(),
            hasRefreshToken: Boolean(tokenRef.current?.refreshToken),
            isTokenRefreshInProgress: tokenRefreshInProgress,
            ...(identity ? identity : {})
          },
          level: 'info'
        });
        Sentry.captureMessage('UnAuthenticated! Tokens were expired?', {
          tags: { ecId, operation: operationData.getFullName() }
        });
      } else {
        Sentry.captureException(networkError, {
          tags: { ecId, operation: operationData.getFullName() }
        });
        console.error(`[Network error]: ${networkError}`);
      }

      if (
        'statusCode' in networkError &&
        !window.location.href.toLocaleLowerCase().includes('network-error')
      ) {
        if (!isUnAuthenticatedError) {
          window.location.assign(
            `/network-error/?code=${networkError.statusCode}&ecid=${ecId}`
          );
        } else if (
          // try to renew token once if failed redirect to network error
          isUnAuthenticatedError &&
          !isTokenValid() &&
          tokenRef.current?.refreshToken &&
          !tokenRefreshInProgress
        ) {
          tokenRefreshInProgress = true;
          Sentry.captureMessage(
            'UnAuthenticated! Token refresh in progress...',
            {
              tags: { ecId }
            }
          );
          refreshTokenWithRetry().then((result) => {
            tokenRefreshInProgress = false;
            if (result !== 'success') {
              window.location.assign(
                `/network-error/?code=${networkError.statusCode}&ecid=${ecId}:${result}`
              );
            }
          });
        } else {
          if (!tokenRefreshInProgress) {
            Sentry.captureMessage('Network error', {
              tags: { ecId, operation: operationData.getFullName() }
            });
            // try redirect to home page once if token is not expired
            if (
              isUnAuthenticatedError &&
              !sessionStorage.getItem('ignored401Once')
            ) {
              sessionStorage.setItem('ignored401Once', 'true');
              window.location.assign('/');
            } else {
              window.location.assign(
                `/network-error/?code=${networkError.statusCode}&ecid=${ecId}`
              );
            }
          }
        }
      }
    }
  });

  const refreshTokenWithRetry = async (retry = 2): Promise<string> => {
    const result = await updateTokenFromRefreshToken(
      tokenRef.current!.refreshToken,
      provider
    );
    Sentry.addBreadcrumb({
      data: { updateTokenResult: result, attempt: retry },
      level: 'info'
    });
    if (result !== 'success') {
      retry--;
      if (retry >= 0) {
        // retry After wakeup usually there could be network issues also VPN connection interupt the network
        await delay(2000);
        return await refreshTokenWithRetry(retry);
      }
    }
    return result;
  };

  return new ApolloClient({
    cache,
    link: authLink
      .concat(sentryGraphContext)
      .concat(errorLink)
      .concat(httpLink),
    name: 'time-intelligence-graph-api',
    version: '1.3',
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network'
      }
    }
  });
};
